import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { RoleBadge } from '../components/RoleSelector';
import { Mail, FolderKanban, RefreshCw, CheckCircle, XCircle, LogIn, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, signInWithWorkOS } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const autoAcceptTriggered = useRef(false);

  const loadInvitation = useCallback(async () => {
    try {
      // This endpoint is public (no auth required) to show invite details
      const response = await api.get(`/invitations/accept/${token}`);
      const data = response.data;

      // Check if invitation is valid
      if (!data.is_valid) {
        setError(data.error || 'This invitation is no longer valid');
        setInvitation(null);
      } else {
        setInvitation(data);
      }
    } catch (error) {
      console.error('Failed to load invitation:', error);
      const message = error.response?.data?.detail || 'Invalid or expired invitation';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvitation();
  }, [loadInvitation]);

  const handleAccept = useCallback(async () => {
    if (!user) {
      // Redirect to WorkOS auth with invite code in state
      // The invite code will be passed through OAuth and returned after auth
      signInWithWorkOS({ inviteCode: token });
      return;
    }

    if (accepting || accepted) return;

    setAccepting(true);
    setError(null);

    try {
      console.log('Accepting invitation with token:', token);
      const response = await api.post(`/invitations/accept/${token}`);
      console.log('Accept response:', response.data);

      // If a new token was issued (org changed), update localStorage
      if (response.data?.new_token) {
        console.log('Updating auth token due to org change');
        localStorage.setItem('token', response.data.new_token);
      }

      setAccepted(true);
      toast.success(`You've joined ${invitation?.project_name || 'the project'}!`);

      // Navigate to the project after a short delay
      const projectId = response.data?.project_id || invitation?.project_id;
      setTimeout(() => {
        // Force page reload to refresh auth context with new token
        window.location.href = `/projects/${projectId}/overview`;
      }, 2000);
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      const message = error.response?.data?.detail || 'Failed to accept invitation';
      toast.error(message);
      setError(message);
      // Reset auto-accept flag so user can try again manually
      autoAcceptTriggered.current = false;
    } finally {
      setAccepting(false);
    }
  }, [user, token, accepting, accepted, invitation, signInWithWorkOS, navigate]);

  // Auto-accept when user lands here after auth (via invite flow)
  // This triggers when user is logged in and invitation is valid
  useEffect(() => {
    const shouldAutoAccept =
      user &&
      !authLoading &&
      invitation &&
      invitation.is_valid &&
      !accepted &&
      !accepting &&
      !error &&
      !autoAcceptTriggered.current;

    if (shouldAutoAccept) {
      console.log('Auto-accepting invitation for user:', user.email);
      autoAcceptTriggered.current = true;
      // Small delay to ensure token is in localStorage
      setTimeout(() => {
        handleAccept();
      }, 100);
    }
  }, [user, authLoading, invitation, accepted, accepting, error, handleAccept]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invitation Invalid</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 items-center">
            <Button onClick={() => navigate('/projects')}>
              Go to Projects
            </Button>
            {user && (
              <p className="text-xs text-muted-foreground">
                Signed in as {user.email}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <CardTitle>You're In!</CardTitle>
            <CardDescription>
              You've successfully joined {invitation?.project_name}. Redirecting...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={() => navigate(`/projects/${invitation?.project_id}/overview`)}>
              Go to Project
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>You've Been Invited</CardTitle>
          <CardDescription>
            {invitation?.invited_by_name || 'Someone'} invited you to join a project
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Project Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-background">
                <FolderKanban className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">{invitation?.project_name || 'Project'}</h3>
                {invitation?.project_description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {invitation.project_description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Role Info */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <span className="text-sm text-muted-foreground">Your role will be</span>
            <RoleBadge role={invitation?.role || 'viewer'} />
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {user ? (
              <Button
                className="w-full"
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept Invitation
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  className="w-full"
                  onClick={handleAccept}
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign In to Accept
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  You'll be redirected to sign in first
                </p>
              </>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/')}
            >
              Decline
            </Button>
          </div>

          {/* Invite Details */}
          <div className="text-xs text-center text-muted-foreground">
            {invitation?.expires_at && (
              <p>This invitation expires on {new Date(invitation.expires_at).toLocaleDateString()}</p>
            )}
            {invitation?.max_uses && (
              <p className="mt-1">
                {invitation.max_uses - (invitation.use_count || 0)} uses remaining
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
